import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FolderKanban, MessageSquareQuote, FileText, Mail, Eye, Clock, User } from 'lucide-react';
import { statsApi } from '@/services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await statsApi.get();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const statCards = stats ? [
    { icon: FolderKanban, label: 'Projects', value: stats.projects, color: 'bg-blue-500/20 text-blue-400', link: '/admin/projects' },
    { icon: MessageSquareQuote, label: 'Testimonials', value: stats.testimonials, color: 'bg-green-500/20 text-green-400', link: '/admin/testimonials' },
    { icon: FileText, label: 'Blog Posts', value: stats.blog_posts, color: 'bg-purple-500/20 text-purple-400', link: '/admin/blog' },
    { icon: Mail, label: 'Contacts', value: stats.contacts, badge: stats.unread_contacts, color: 'bg-orange-500/20 text-orange-400', link: '/admin/contacts' },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-white/60">Welcome back to Arman Kitchens admin panel</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-white/5 animate-pulse rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={card.link}
                data-testid={`stat-card-${card.label.toLowerCase()}`}
                className="block bg-white/5 border border-white/10 p-6 hover:border-gold/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 rounded-lg ${card.color} flex items-center justify-center`}>
                    <card.icon size={24} />
                  </div>
                  {card.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {card.badge} new
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <p className="text-3xl font-bold text-white">{card.value}</p>
                  <p className="text-white/60">{card.label}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/projects"
            className="flex items-center gap-4 bg-white/5 border border-white/10 p-4 hover:border-gold/50 transition-colors"
          >
            <div className="w-10 h-10 bg-gold/20 text-gold rounded-lg flex items-center justify-center">
              <FolderKanban size={20} />
            </div>
            <div>
              <p className="text-white font-medium">Add New Project</p>
              <p className="text-white/40 text-sm">Showcase your latest work</p>
            </div>
          </Link>

          <Link
            to="/admin/blog"
            className="flex items-center gap-4 bg-white/5 border border-white/10 p-4 hover:border-gold/50 transition-colors"
          >
            <div className="w-10 h-10 bg-gold/20 text-gold rounded-lg flex items-center justify-center">
              <FileText size={20} />
            </div>
            <div>
              <p className="text-white font-medium">Write Blog Post</p>
              <p className="text-white/40 text-sm">Share your expertise</p>
            </div>
          </Link>

          <Link
            to="/"
            target="_blank"
            className="flex items-center gap-4 bg-white/5 border border-white/10 p-4 hover:border-gold/50 transition-colors"
          >
            <div className="w-10 h-10 bg-gold/20 text-gold rounded-lg flex items-center justify-center">
              <Eye size={20} />
            </div>
            <div>
              <p className="text-white font-medium">View Website</p>
              <p className="text-white/40 text-sm">See your changes live</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent Contacts */}
      {stats?.recent_contacts?.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent Contacts</h2>
            <Link to="/admin/contacts" className="text-gold text-sm hover:underline">View all</Link>
          </div>
          <div className="bg-white/5 border border-white/10 divide-y divide-white/10">
            {stats.recent_contacts.map((contact) => (
              <Link
                key={contact.id}
                to="/admin/contacts"
                className={`flex items-center justify-between p-4 hover:bg-white/5 transition-colors ${
                  !contact.read ? 'border-s-2 border-gold' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-gold text-sm font-bold flex-shrink-0">
                    <User size={14} />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${contact.read ? 'text-white/60' : 'text-white'}`}>
                      {contact.name}
                    </p>
                    <p className="text-white/40 text-xs truncate max-w-[200px]">{contact.message}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-white/30 text-xs flex-shrink-0">
                  <Clock size={12} />
                  {formatDate(contact.created_at)}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
